import React, {useEffect, useRef, useState} from "react";
import PropTypes from 'prop-types';

import {dataManager} from "../managers/DataManager";
import {flowManager} from "../managers/FlowManager";
import {userManager} from "../managers/UserManager";
import {StepHeader} from "../components/StepHeader";
import {StepCategory} from "../components/StepCategory";
import {TileCheckbox} from "../components/TileCheckbox";
import {SubCategoryTile} from "../components/SubCategoryTile";
import {dataKeys} from "../enums/dataKeys";

HomeyProCategoryStep.propTypes = {
  index: PropTypes.number
}

export function HomeyProCategoryStep(props) {
  const name = HomeyProCategoryStep.name;
  const data = dataManager.store(state => state.data); // Data is updated on state change.
  const steps = flowManager.store(state => state.steps); // Steps is updated on state change.
  const stepRef = useRef(null);
  const updateAvailableRef = useRef(null);
  const underVoltageRatioRef = useRef(null);
  const [underVoltageRatio, setUnderVoltageRatio] = useState("");
  const isMountedRef = useRef(true);
  const [checkValue, setCheckValue] = useState("");
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const homey = userManager.getSelectedHomey();
  const options = {};

  // On mount, focus the step and scroll into view.
  useEffect(() => {
    stepRef.current.focus({ preventScroll: true });
    window.scrollTo({
      top: (stepRef.current.offsetTop - 120),
      behavior: "smooth"
    });
  }, [])

  useEffect(() => {
    const entry = data.find(item => item.name === name);
    if (entry) {
      setCheckValue(entry.value);
    }
  }, [data, name])

  useEffect(() => {
    if (!homey) return;
    isMountedRef.current = true;

    // On mount, check if the selected Homey Pro is up-to-date
    function getUpdateAvailable() {
      homey.authenticate()
        .then((homeyApi) => {
          return homeyApi.updates.getUpdates();
        })
        .then((updates) => {
          if (!isMountedRef.current) return
          if (Array.isArray(updates)) {
            setUpdateAvailable(updates.length > 0);
          } else {
            console.error("Updates is not an array: ", updates);
          }
        })
        .catch((error) => {
          console.error("Couldn't get updates:", error.message);
        });
    }

    // On mount, if a Homey Pro (Early 2023) is selected, get the under voltage ratio from system info.
    function getUnderVoltageRatio() {
      if (homey.model !== "homey5q" && homey.model !== "homey6q" && homey.model !== "homey7q") return;
      homey.authenticate()
        .then((homeyApi) => {
          const systemInfoPromise = homeyApi.system.getInfo();
          dataManager.pendingPromises.systemInfoPromise = systemInfoPromise;
          return systemInfoPromise;
        })
        .then((systemInfo) => {
          setUnderVoltageRatio(systemInfo.videoCoreUnderVoltageCurrentlyRatio);
        })
        .catch((error) => {
          console.error("Couldn't get under voltage ratio:", error);
          setUnderVoltageRatio("Couldn't get under voltage ratio");
        })
        .finally(() => {
          delete dataManager.pendingPromises.systemInfoPromise;
        });
    }

    if (!updateAvailableRef.current) {
      updateAvailableRef.current = true;
      getUpdateAvailable();
    }

    if (!underVoltageRatioRef.current) {
      underVoltageRatioRef.current = true;
      getUnderVoltageRatio();
    }

    return () => {
      isMountedRef.current = false;
    }
  }, [homey, name, props.index])

  function handleSelect(event) {
    const value = event.target.value;
    const data = options[value].data;
    const action = data.action;
    const entry = { name, value, underVoltageRatio, ...data };

    flowManager.nextActionStep(props.index, entry, action);
  }

  // Add-ons
  options[`addOns-0`] = {
    title: "Homey Bridge",
    icon: "homey-bridge",
    data: { ...HomeyProCategoryStep.homeyBridge }
  };
  if (homey.model === "homey5q" || homey.model === "homey7q") {
    options[`addOns-1`] = {
      title: "Homey Portal",
      icon: "homey-portal",
      data: { ...HomeyProCategoryStep.homeyPortal }
    };
  }
  options[`addOns-2`] = {
    title: "Homey Energy Dongle",
    icon: "energy-dongle",
    data: { ...HomeyProCategoryStep.homeyEnergyDongle }
  };
  options[`addOns-3`] = {
    title: "Homey Pro Ethernet Adapter",
    icon: "ethernet-adapter",
    data: { ...HomeyProCategoryStep.homeyProEthernetAdapter }
  };

  // Apps & Devices
  options[`appsAndDevices-0`] = {
    title: "Apps",
    icon: "apps",
    data: { ...HomeyProCategoryStep.apps }
  };
  options[`appsAndDevices-1`] = {
    title: "Devices",
    icon: "devices",
    data: { ...HomeyProCategoryStep.devices }
  };

  // Automation
  options[`automation-0`] = {
    title: "Flow",
    icon: "flow",
    data: { ...HomeyProCategoryStep.flow }
  };
  options[`automation-1`] = {
    title: "Advanced Flow",
    icon: "advanced-flow",
    data: { ...HomeyProCategoryStep.advancedFlow }
  };
  options[`automation-2`] = {
    title: "Logic",
    icon: "logic",
    data: { ...HomeyProCategoryStep.logic }
  };

  // Monitoring
  options[`monitoring-0`] = {
    title: "Insights",
    icon: "insights",
    data: { ...HomeyProCategoryStep.insights }
  };
  options[`monitoring-1`] = {
    title: "Energy",
    icon: "energy",
    data: { ...HomeyProCategoryStep.energy }
  };
  options[`monitoring-2`] = {
    title: "Dashboards",
    icon: "dashboards",
    data: { ...HomeyProCategoryStep.dashboards }
  };

  // Platform
  options['platform-0'] = {
    title: "Mobile App",
    icon: "phone",
    data: { ...HomeyProCategoryStep.mobileApp }
  };
  options['platform-1'] = {
    title: "Web App",
    icon: "webapp",
    data: { ...HomeyProCategoryStep.webApp }
  };

  // Voice Assistants
  options[`voiceAssistants-0`] = {
    title: "Amazon Alexa",
    icon: "alexa",
    data: { ...HomeyProCategoryStep.alexa }
  };
  options[`voiceAssistants-1`] = {
    title: "Google Assistant",
    icon: "google",
    data: { ...HomeyProCategoryStep.google }
  };
  options[`voiceAssistants-2`] = {
    title: "Siri",
    icon: "siri",
    data: { ...HomeyProCategoryStep.siri }
  };

  // Warranty
  options[`warranty-0`] = {
    title: "Defects",
    icon: "defects",
    data: { ...HomeyProCategoryStep.defects }
  };
  options[`warranty-1`] = {
    title: "Returns",
    icon: "returns",
    data: { ...HomeyProCategoryStep.returns }
  };

  // Wireless Technologies
  options[`wireless-0`] = {
    title: "433 MHz",
    icon: "433mhz",
    data: { ...HomeyProCategoryStep["433mhz"] }
  };
  if (homey.model !== "homey5q" && homey.model !== "homey7q") {
    options[`wireless-1`] = {
      title: "868 MHz",
      icon: "868mhz",
      data: { ...HomeyProCategoryStep["868mhz"] }
    };
  }
  options[`wireless-2`] = {
    title: "Bluetooth",
    icon: "bluetooth",
    data: { ...HomeyProCategoryStep.bluetooth }
  };
  if (homey.model === "homey5q" || homey.model === "homey7q") {
    options[`wireless-3`] = {
      title: "Matter",
      icon: "matter",
      data: { ...HomeyProCategoryStep.matter }
    };
  }
  options[`wireless-4`] = {
    title: "Infrared",
    icon: "infrared",
    data: { ...HomeyProCategoryStep.infrared }
  };
  if (homey.model === "homey5q" || homey.model === "homey7q") {
    options[`wireless-5`] = {
      title: "Thread",
      icon: "thread",
      data: { ...HomeyProCategoryStep.thread }
    };
  }
  options[`wireless-6`] = {
    title: "Wi-Fi",
    icon: "wifi",
    data: { ...HomeyProCategoryStep.wifi }
  };
  options[`wireless-7`] = {
    title: "Zigbee",
    icon: "zigbee",
    data: { ...HomeyProCategoryStep.zigbee }
  };
  options[`wireless-8`] = {
    title: "Z-Wave",
    icon: "z-wave",
    data: { ...HomeyProCategoryStep.zwave }
  };

  // More
  options[`more-0`] = {
    title: "Family",
    icon: "family",
    data: { ...HomeyProCategoryStep.family }
  };
  options[`more-3`] = {
    title: "Backups",
    icon: "backups",
    data: { ...HomeyProCategoryStep.backups }
  };
  options[`more-4`] = {
    title: "Other",
    icon: "other",
    data: { ...HomeyProCategoryStep.other }
  };

  return (
    <>
      <div ref={stepRef} tabIndex={0} className="support-form-step">
        <StepHeader
          index={props.index}
          question="How can we help you with Homey Pro?"
          description="Choose the category that describes your question or problem best."
        >
          <div className={`support-form__warning ${updateAvailable ? "is-visible" : ""}`}>
            <div className="support-form__warning-icon"/>
            <div>
              <p className="support-form__warning-title">Homey Pro’s software is out of date.</p>
              <p className="support-form__warning-text">
                Updating Homey Pro to the latest firmware version may resolve your issue.
              </p>
            </div>
          </div>
        </StepHeader>
        <div className="support-form-step__categories">
          {(homey.model === "homey5q" || homey.model === 'homey6q' || homey.model === 'homey7q') && ( // Only show add-ons for homey5q and later
            <StepCategory heading="Add-ons">
              {Object.entries(options).map(([value, option]) => {
                return value.startsWith("addOns") && (
                  <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                    <SubCategoryTile title={option.title} icon={option.icon}/>
                  </TileCheckbox>
                )
              })}
            </StepCategory>
          )}
          <StepCategory heading="Apps & Devices">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("appsAndDevices") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Automation">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("automation") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Monitoring">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("monitoring") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Platform">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("platform") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Voice Assistants">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("voiceAssistants") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Warranty">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("warranty") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Wireless Technologies">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("wireless") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="More">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("more") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
        </div>
      </div>
      {steps.length === props.index + 1 && (
        <div className="support-form-step">
          <StepHeader
            index={props.index + 1}
            question="Select one of the above options."
          />
        </div>
      )}
    </>
  );
}

HomeyProCategoryStep.homeyBridge = {
  action: "homeyBridge",
  [dataKeys.labels]: ["add-ons", "homey-bridge"],
  [dataKeys.path]: ["Add-ons", "Homey Bridge"]
}
HomeyProCategoryStep.homeyPortal = {
  action: "homeyPortal",
  [dataKeys.labels]: ["add-ons", "homey-portal"],
  [dataKeys.path]: ["Add-ons", "Homey Portal"]
}
HomeyProCategoryStep.homeyEnergyDongle = {
  action: "homeyEnergyDongle",
  [dataKeys.labels]: ["add-ons", "homey-energy-dongle"],
  [dataKeys.path]: ["Add-ons", "Homey Energy Dongle"]
}
HomeyProCategoryStep.homeyProEthernetAdapter = {
  action: "homeyProEthernetAdapter",
  [dataKeys.labels]: ["add-ons", "homey-pro-ethernet-adapter"],
  [dataKeys.path]: ["Add-ons", "Homey Pro Ethernet Adapter"]
}
HomeyProCategoryStep.apps = {
  action: "apps",
  [dataKeys.labels]: ["apps"],
  [dataKeys.path]: ["Apps"]
}
HomeyProCategoryStep.devices = {
  action: "devices",
  [dataKeys.labels]: ["devices"],
  [dataKeys.path]: ["Devices"]
}
HomeyProCategoryStep.flow = {
  action: "flow",
  [dataKeys.labels]: ["flow"],
  [dataKeys.path]: ["Flow"]
}
HomeyProCategoryStep.advancedFlow = {
  action: "advancedFlow",
  [dataKeys.labels]: ["advanced-flow"],
  [dataKeys.path]: ["Advanced Flow"]
}
HomeyProCategoryStep.logic = {
  action: "logic",
  [dataKeys.labels]: ["logic"],
  [dataKeys.path]: ["Logic"]
}
HomeyProCategoryStep.insights = {
  action: "insights",
  [dataKeys.labels]: ["insights"],
  [dataKeys.path]: ["Insights"]
}
HomeyProCategoryStep.energy = {
  action: "energy",
  [dataKeys.labels]: ["energy"],
  [dataKeys.path]: ["Energy"]
}
HomeyProCategoryStep.dashboards = {
  action: "dashboards",
  [dataKeys.labels]: ["dashboards"],
  [dataKeys.path]: ["Dashboards"]
}
HomeyProCategoryStep.mobileApp = {
  action: "mobileApp",
  [dataKeys.labels]: ["mobile-app"],
  [dataKeys.path]: ["Mobile App"]
}
HomeyProCategoryStep.webApp = {
  action: "webApp",
  [dataKeys.labels]: ["web-app"],
  [dataKeys.path]: ["Web App"]
}
HomeyProCategoryStep.alexa = {
  action: "alexa",
  [dataKeys.labels]: ["alexa"],
  [dataKeys.path]: ["Amazon Alexa"]
}
HomeyProCategoryStep.google = {
  action: "google",
  [dataKeys.labels]: ["google-assistant"],
  [dataKeys.path]: ["Google Assistant"]
}
HomeyProCategoryStep.siri = {
  action: "siri",
  [dataKeys.labels]: ["siri"],
  [dataKeys.path]: ["Siri"]
}
HomeyProCategoryStep.defects = {
  action: "defects",
  [dataKeys.labels]: ["defects"],
  [dataKeys.path]: ["Defects"]
}
HomeyProCategoryStep.returns = {
  action: "returns",
  [dataKeys.labels]: ["returns"],
  [dataKeys.path]: ["Returns"]
}
HomeyProCategoryStep["433mhz"] = {
  action: "433mhz",
  [dataKeys.labels]: ["433mhz"],
  [dataKeys.path]: ["433 MHz"]
}
HomeyProCategoryStep["868mhz"] = {
  action: "868mhz",
  [dataKeys.labels]: ["868mhz"],
  [dataKeys.path]: ["868 MHz"]
}
HomeyProCategoryStep.bluetooth = {
  action: "bluetooth",
  [dataKeys.labels]: ["bluetooth"],
  [dataKeys.path]: ["Bluetooth"]
}
HomeyProCategoryStep.infrared = {
  action: "infrared",
  [dataKeys.labels]: ["infrared"],
  [dataKeys.path]: ["Infrared"]
}
HomeyProCategoryStep.wifi = {
  action: "wifi",
  [dataKeys.labels]: ["wifi"],
  [dataKeys.path]: ["Wi-Fi"]
}
HomeyProCategoryStep.zigbee = {
  action: "zigbee",
  [dataKeys.labels]: ["zigbee"],
  [dataKeys.path]: ["Zigbee"]
}
HomeyProCategoryStep.zwave = {
  action: "zwave",
  [dataKeys.labels]: ["z-wave"],
  [dataKeys.path]: ["Z-Wave"]
}
HomeyProCategoryStep.family = {
  action: "family",
  [dataKeys.labels]: ["family"],
  [dataKeys.path]: ["Family"]
}
HomeyProCategoryStep.backups = {
  action: "backups",
  [dataKeys.labels]: ["backups"],
  [dataKeys.path]: ["Backups"]
}
HomeyProCategoryStep.other = {
  action: "other",
  [dataKeys.labels]: ["other"],
  [dataKeys.path]: ["Other"]
}

HomeyProCategoryStep.matter = {
  action: "matter",
  [dataKeys.labels]: ["matter"],
  [dataKeys.path]: ["Matter"]
}
HomeyProCategoryStep.thread = {
  action: "thread",
  [dataKeys.labels]: ["thread"],
  [dataKeys.path]: ["Thread"]
}

